import { Injectable } from '@angular/core';
import { AuthenticationService } from './authentication.service';
import { Subject } from 'rxjs';
import { UsbTokenService } from './usbtoken.service';
import { PdfSignatureData } from '../models/pdfsignaturedata';
import * as signalR from "@microsoft/signalr";
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { environment } from 'src/environments/environment';
import { HttpErrorResponse } from '@angular/common/http';
import { LoggingService } from './logging.service';
// In app component we require signalr

@Injectable({
  providedIn: 'root'
})

export class PkisigningService {
  accessToken!: string;

  constructor(private usbToken: UsbTokenService, private oidcSecurityService: OidcSecurityService, private loggingService: LoggingService) {
    let slot = document.cookie.split('; ').find(row => row.startsWith("slot="))?.split('=')[1];
    if (slot)
      this.postfix = "x-ms-routing-name=" + slot;
  }

  private signingHub!: signalR.HubConnection;
  private cert = null;
  private starting = false;
  private postfix: string = "";

  private subject = new Subject<string[]>();
  pkisEvent = this.subject.asObservable();


  public start(sessionId: string, sessionKey: string) {
    this.oidcSecurityService.getAccessToken().subscribe((token) => this.accessToken = token);

    if (this.starting) return;
    this.starting = true;


    this.signingHub = new signalR.HubConnectionBuilder()
      .withUrl(environment.signingApiUrl + "/signinghub?access_tokent=" + this.accessToken + "&" + this.postfix)
      .withAutomaticReconnect([1000, 2000, 5000, 10000, 10000, 10000])
      .build();
    this.signingHub.serverTimeoutInMilliseconds = 240000; //4 minutes

    this.signingHub.on('SignHash', (b64encoded) => {
      if (this.lastOpenedPopup) this.lastOpenedPopup.close();
      this.subject.next(["signHash"]);
      let bytes = new Uint8Array(atob(b64encoded).split("")
        .map(function (c) {
          return c.charCodeAt(0);
        }));

      let x = this;
      this.usbToken.Sign(x.cert, { type: 'SHA-256', value: bytes }, { lang: 'en' })
        .then(function (response: any) {
          x.signingHub.invoke("PushSignature", btoa(String.fromCharCode.apply(null, response.value))).catch((error) => x.loggingService.logException(error));
        },
          function (err: HttpErrorResponse) {
            x.subject.next(["error", err.message]);
            x.signingHub.stop().catch((error) => x.loggingService.logException(error));
          });
    });

    this.signingHub.on('GetCertificate', () => {
      let x = this;
      this.subject.next(["getCertificate"]);
      this.usbToken.Use("auto").catch((error) => this.loggingService.logException(error));
      this.usbToken.Debug()
        .then(function (response: string | unknown): void {
          if (typeof (response) === "string" && response.indexOf("No implementation") > -1) {
            x.subject.next(["error", "no_implementation"]);
          } else {
            x.tryGetCertificate();
          }
        })
        .catch((error) => x.loggingService.logException(error));
    });

    this.signingHub.on('GetPdfSignatureData', () => {
      this.subject.next(["getPdfSignatureData"]);
    });

    this.signingHub.on('ChangeOfSignatureProviderRequired', () => {
      if (this.lastOpenedPopup) this.lastOpenedPopup.close();
      this.subject.next(["changeOfSignatureProviderRequired"]);
    });

    this.signingHub.on('GetOneTimePassword', () => {
      if (this.lastOpenedPopup) this.lastOpenedPopup.close();
      this.subject.next(["getOneTimePassword"]);
    });

    this.signingHub.on('ShowMobilePermissionRequest', (reference) => {
      if (this.lastOpenedPopup) this.lastOpenedPopup.close();
      this.subject.next(["showMobilePermissionRequest", reference]);
    });

    this.signingHub.on('DownloadData', () => {
      this.subject.next(["downloadData"]);
    });

    this.signingHub.on('ShowAvailableCertificates', (certs) => {
      if (this.lastOpenedPopup) this.lastOpenedPopup.close();
      this.subject.next(['showAvailableCertificates', certs]);
    });

    this.signingHub.on('ShowError', (e) => {
      if (this.lastOpenedPopup) this.lastOpenedPopup.close();
      this.subject.next(["error", e]);
    });

    this.signingHub.on("ShowCscAuthentication", (url) => {
      this.subject.next(['authenticateCsc', url]);
      this.showCscAuthenticationPopup(url);
    });

    this.signingHub.on('GetCscCertificateAuthorization', (format, label, description) => {
      this.subject.next(["getCscCertificateAuthorization", format, label, description]);
    });

    let currentClass = this;

    this.signingHub.start()
      .then(function () {
        currentClass.signingHub.invoke('initSession', sessionId, sessionKey).catch((error) => currentClass.loggingService.logException(error));
      }).catch((e) => {
        currentClass.subject.next(['error', e]);
      });
  }
  /**
   * This preopens a certain Csc authentication window in a hidden popup, this means the user can go through the authorization without having popup blocker problems
   * Mostly for safari browsers this was needed
   */
  public preOpenCscPopup() {
    this.lastOpenedPopup = window.open("about:blank", "cscAuthentication");
  }
  
  tryGetCertificate() {
    let x: any = this;
    x.usbToken.GetCertificate({ lang: 'en' })
      .then(
        function (response: any) {
          x.cert = response;
          x.signingHub.invoke("PushCertificate", btoa(String.fromCharCode.apply(null, x.cert.encoded)));
        },
        function (err: any) {

          x.subject.next(["error", err.message]);
        }
      );
  }

  checkPluginInstallation() {
    let x = this;
    this.usbToken.Debug().then((response: any) => {
      if (response.indexOf("No implementation") > -1) {
        x.subject.next(["error", "no_pkisigningplugin"]);
      }
    }).catch((error) => this.loggingService.logException(error));
  }

  setPdfSignatureData(signatureData: PdfSignatureData) {
    let currentClass = this;
    

    this.signingHub.invoke('SetPdfSignatureData',
      {
        location: signatureData.location,
        reason: signatureData.reason,
        svgData: signatureData.svg,
        signatureImage: signatureData.image,
        signField: JSON.stringify(signatureData.signatureField),
        signatureFields: JSON.stringify(signatureData.signatureFields),
        latitude: signatureData.lat,
        longitude: signatureData.lon,
        embedTextOnImage: signatureData.embedTimestampInSvg
      })
      .catch((e) => {
        currentClass.subject.next(["error", e]);
      });
  }

  selectCertificateToUse(certId: any) {
    let currentClass = this;
    this.signingHub.invoke('SelectCertificateToUse', certId)
      .catch((e) => {
        currentClass.subject.next(["error", e]);
      });
  }

  validateOneTimePassword(qes: string) {
    let currentClass = this;
    this.signingHub.invoke('ValidateOneTimePassword', qes)
      .catch((e) => {
        currentClass.subject.next(["error", e]);
      });
  }

  resendOneTimePassword() {
    let currentClass = this;
    this.signingHub.invoke('ResendOneTimePassword')
      .catch((e) => {
        currentClass.subject.next(["error", e]);
      });
  }

  getSignatureProviders() {
    let currentClass = this;
    this.signingHub.invoke('GetAvailableSignatureProviders')
      .then(function (data) {
        currentClass.subject.next(["signatureProviders", data]);
      })
      .catch((e) => {
        currentClass.subject.next(["error", e]);
      });
  }

  changeSignatureProvider(providertype: string, parameters: string[]) {
    let currentClass = this;
    this.signingHub.invoke('ChangeSignatureProvider', providertype, parameters)
      .catch((e) => {
        currentClass.subject.next(["error", e]);
      })
  }

  private showCscAuthenticationPopup(url: string) {
    if (!this.receiveMessage)
      this.receiveMessage.bind(this);

    if (window.addEventListener) {
      window.addEventListener("message", this.receiveMessage, false);
    } else {
      (<any>window).attachEvent("onmessage", this.receiveMessage);
    }

    if (this.lastOpenedPopup === undefined)
      window.open(url, "cscAuthentication"/*, "location=0,height=800,width=800"*/);
    else
      this.lastOpenedPopup.location = url;
    this.starting = false;
  }

  private lastOpenedPopup: any;
  private receiveMessage: any = (event: any) => {
    origin = event.origin.split('/');
    if (origin.length < 3 || origin[0] !== "https:" || (origin[2].indexOf("engine.pkisigning.io") === -1 || origin[2].indexOf("engine.pkisigning.io") > 4) && (origin[2].indexOf("localhost") === -1 || origin[2].indexOf("localhost") > 0)) return;
    let values = event.data.substring(1).split('&');
    let code;
    for (let i = 0; i < values.length; i++) {
      values[i] = values[i].split('=');
      if (values[i][0] === "code") {
        code = values[i][1];
        break;
      }
    }
    this.setCscAuthorizationCode(code);

    if (event.data.indexOf("state=authenticate") == -1) {
      event.source.close();
      this.lastOpenedPopup = undefined;
    } else {
      this.lastOpenedPopup = event.source;
    }
  }

  private setCscAuthorizationCode(code: string) {
    this.subject.next(['cscAuthenticated']);
    let currentClass = this;
    this.signingHub.invoke('SetCscAuthorizationCode', code)
      .catch(function (e) {
        currentClass.subject.next(["error", e]);
      });
    if (window.removeEventListener) {
      window.removeEventListener("message", this.receiveMessage, false);
    } else {
      (<any>window).detachEvent("onmessage", this.receiveMessage);
    }
  }

  stop() {
    if (this.signingHub)
      this.signingHub.stop().catch((error) => this.loggingService.logException(error));
    this.starting = false;
  }

}
